import React, { Fragment, useState, useEffect, useRef } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { useLocation } from '@reach/router'
import { MDBRow, MDBCol, MDBNavbar, MDBNavbarBrand, MDBNavbarNav, MDBNavItem, MDBNavbarToggler, MDBCollapse, MDBContainer } from 'mdbreact'
import MDBDropdownItem from './MDBDropdownItem.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faArrowLeftLong, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { GatsbyImage } from 'gatsby-plugin-image'
import { goBackNav, resolvePath } from '../utils/helpersClient.js'
import CustomLink from './CustomLink'
import NavLogo from './navLogo.js'
import NavLogoAlt from './navLogoAlt.js'

const NavBarPage = () => {
  // Use Gatsby's location hook to access the current URL path and set navbarHeight for each site
  const location = useLocation()
  
  const data = useStaticQuery(graphql`
    query NavbarQuery {
      allMarkdownRemark(filter: { frontmatter: { name: { eq: "navbar" } } }) {
        edges {
          node {
            frontmatter {
              companynav
              section {
                collg
                feature {
                  subtitle
                  description
                  link
                  image {
                    childImageSharp {
                      gatsbyImageData(layout: FIXED, height: 40, quality: 90)
                    }
                  }
                  alttext
                }                
                item {
                  smorder
                  lgorder
                  title
                  link
                  image {
                    childImageSharp {
                      gatsbyImageData(layout: FIXED, height: 40, quality: 90)
                    }
                  }
                  alttext
                  subitem {
                    link
                    title
                  }
                }
                title
                subtitle
                more
                link
              }
            }
          }
        }
      }
    }
  `)

  // =========================================================================
  // STATE DECLARATIONS
  // =========================================================================

  const [collapsed, setCollapsed] = useState(false)  // Controls whether the mobile menu is expanded or collapsed
  const [isScrolled, setIsScrolled] = useState(false)  // Tracks whether the user has scrolled the page (for styling)
  const [isMenuItemClicked, setIsMenuItemClicked] = useState(false)  // Tracks whether a dropdown menu item has been clicked
  const [activeDropdown, setActiveDropdown] = useState('')  // Stores which dropdown menu is currently active
  const scrollbarWidth = useRef(0)  // Ref to store the browser's scrollbar width (doesn't trigger re-renders)
  const pathPriorities = {
    'platforms': 'Solutions', // /platforms/ belongs to solutions dropdown
    'blog': 'Blog'            // /blog/ belongs to top-level blog item
  };

  // =========================================================================
  // INITIAL SETUP - SCROLLBAR WIDTH CALCULATION & CSS INJECTION
  // =========================================================================

  // This effect runs once on component mount
  useEffect(() => {
    // Calculate scrollbar width using DOM manipulation
    // Creates temporary elements to measure difference between inner and outer widths
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll'
    document.body.appendChild(outer)

    const inner = document.createElement('div')
    outer.appendChild(inner)

    // Calculate width and store in ref
    scrollbarWidth.current = outer.offsetWidth - inner.offsetWidth
    document.body.removeChild(outer)

    // Inject CSS style to prevent content jump when scrollbar is hidden
    // This style adds padding equal to scrollbar width when body is locked
    const style = document.createElement('style')
    style.textContent = `
      body.scroll-lock {
        overflow: hidden;
        padding-right: ${scrollbarWidth.current}px;
      }

      /* Add CSS variable for scrollbar width */
      :root {
        --scrollbar-width: ${scrollbarWidth.current}px;
      }
    `
    document.head.appendChild(style)

    // Cleanup function runs when component unmounts
    return () => {
      document.head.removeChild(style)
      document.body.classList.remove('scroll-lock')
    }
  }, []) // Empty dependency array means this runs once on mount

  // =========================================================================
  // URL PARSING FOR NAVIGATION STATE
  // =========================================================================

  // This effect also runs once on mount and when location changes
  useEffect(() => {
    // Extract the first segment of the URL path to determine current section
    const path = location.pathname.substring(1)
    const firstSegment = path.split('/')[0]
  }, [location])

  // =========================================================================
  // SCROLL EVENT HANDLER
  // =========================================================================

  useEffect(() => {
    // Function to check scroll position and update state
    const handleScroll = () => {
      // Only apply scroll effects on desktop/tablet (992px and above)
      if (window.innerWidth >= 992) {
        setIsScrolled(window.scrollY > 0)
      }
    }

    // Register scroll event listener
    window.addEventListener('scroll', handleScroll)
    // Call handler immediately to set initial state
    handleScroll()

    // Cleanup function removes event listener when component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // =========================================================================
  // UTILITY FUNCTIONS
  // =========================================================================

  // Applies scroll lock to body
  const lockScroll = () => {
    document.body.classList.add('scroll-lock')
  }

  // Removes scroll lock from body
  const unlockScroll = () => {
    document.body.classList.remove('scroll-lock')
  }

  // Handles closing any open dropdown
  const handleDropdownClose = () => {
    setIsMenuItemClicked(false)
    setActiveDropdown('')
    unlockScroll()
  }

  // Toggles mobile menu (hamburger menu) state
  const handleTogglerClick = () => {
    if (collapsed) {
      unlockScroll()
    }
    setCollapsed(!collapsed)
  } 

    // Closes mobile menu (hamburger menu) state
  const handleNavbarBrandClick = () => {
    if (collapsed) {
      unlockScroll()
      setCollapsed(!collapsed)
    }
  }

  // =========================================================================
  // DROPDOWN MENU INTERACTION HANDLERS
  // =========================================================================

  // Controls behavior when a dropdown menu item is clicked
  const handleMenuItemClick = (menuTitle, e) => {
    e && e.preventDefault();
    
    // If clicking the currently open dropdown, close it
    if (activeDropdown !== menuTitle) {
      // If another dropdown is already open, close it first
      if (isMenuItemClicked) {
        unlockScroll()
      }

      // Then set the new dropdown as active
      setActiveDropdown(menuTitle)
      setIsMenuItemClicked(true)
      lockScroll()
    }
  }

  // =========================================================================
  // CLICK OUTSIDE HANDLER
  // =========================================================================

  // Closes dropdown when clicking outside of it
  useEffect(() => {
    // Only add handler when a dropdown is active
    if (isMenuItemClicked) {
      const handleClickOutside = (e) => {

        const isDropdownClick = e.target.closest('.dropdown-ug')
        const isToggleClick = e.target.closest('.dropdown-toggle')
        const isNavbarClick = e.target.closest('.navbar-collapse')

        // Only apply click-outside behavior on mobile
        // Close dropdown if clicking outside dropdown and toggle
        if (!isDropdownClick && !isToggleClick && !isNavbarClick) {
          handleDropdownClose()
        }
        // Also close if clicking in dropdown but not on interactive elements
        else if (window.innerWidth < 992 && isDropdownClick && !e.target.closest('a, button, .dropdown-item')) {
          handleDropdownClose()
        }
      }

      // Add global click listener
      document.addEventListener('click', handleClickOutside)

      // Cleanup function removes listener
      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }
  }, [isMenuItemClicked]) // Re-runs when dropdown state changes

  // =========================================================================
  // CLEANUP ON UNMOUNT
  // =========================================================================

  useEffect(() => {
    // Ensure scroll is unlocked when component unmounts
    return () => {
      unlockScroll()
    }
  }, [])

  // =========================================================================
  // NAVBAR POSITION STABILIZATION
  // =========================================================================

  // Prevents navbar jump when scrollbar appears/disappears
  useEffect(() => {
    const navbar = document.querySelector('.navbar');

    // Function to adjust navbar padding based on scrollbar state
    const applyNavbarCompensation = () => {
      if (document.body.classList.contains('scroll-lock') && navbar) {
        // Save original padding before modifying
        if (!navbar.getAttribute('data-original-padding-right')) {
          const currentPadding = window.getComputedStyle(navbar).paddingRight;
          navbar.setAttribute('data-original-padding-right', currentPadding);
        }

        // Add scrollbar width to padding to prevent content shift
        const originalPadding = parseInt(navbar.getAttribute('data-original-padding-right') || '0', 10);
        navbar.style.paddingRight = `${originalPadding + scrollbarWidth.current}px`;
      } else if (navbar) {
        // Restore original padding when scroll is unlocked
        const originalPadding = navbar.getAttribute('data-original-padding-right');
        if (originalPadding) {
          navbar.style.paddingRight = originalPadding;
        } else {
          navbar.style.removeProperty('padding-right');
        }
      }
    };

    // Create observer to watch for scroll-lock class changes on body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class' &&
          (mutation.target.classList.contains('scroll-lock') ||
            !mutation.target.classList.contains('scroll-lock'))) {
          applyNavbarCompensation();
        }
      });
    });

    // Start observing body element for class changes
    observer.observe(document.body, { attributes: true });

    // Apply compensation initially
    applyNavbarCompensation();

    // Cleanup function disconnects observer and restores original state
    return () => {
      observer.disconnect();
      if (navbar) {
        navbar.style.removeProperty('padding-right');
        navbar.removeAttribute('data-original-padding-right');
      }
    };
  }, []);

  // =========================================================================
  // UPDATED HELPER FUNCTIONS FOR NAVIGATION HIGHLIGHTING
  // =========================================================================

  // NEW HELPER FUNCTION: Check if a section is active based on items and subitems
  const isSectionActive = (section) => {
    // Get current path without leading slash
    const currentPath = location.pathname.substring(1);
    const firstPathSegment = currentPath.split('/')[0];
    
    // SPECIAL HARDCODED CASES
    // Blog special case - if path starts with 'blog', only the Blog top-level item should be active
    if (firstPathSegment === 'blog') {
      // Check if this is the Blog section (with any link format)
      return section.title === 'Blog' && (!section.item || section.item.length === 0);
    }
    
    // Platforms special case - if path starts with 'platforms', only the Solutions dropdown should be active
    if (firstPathSegment === 'platforms') {
      return section.title === 'Solutions';
    }
    
    // For all other URLs, use the regular priority logic
    if (pathPriorities[firstPathSegment]) {
      const prioritySection = pathPriorities[firstPathSegment];
      
      // If this is a top-level item with the matching title
      if ((!section.item || section.item.length === 0) && 
          section.title === prioritySection) {
        return true;
      }
      
      // If this is a dropdown, only activate if it has priority
      if (section.item && section.item.length > 0) {
        return section.title === prioritySection;
      }
      
      // Otherwise, this section shouldn't be active
      return false;
    }
    
    // Regular logic for paths without priority assignments
    // 1. Handle top-level items (no dropdown)
    if (!section.item || section.item.length === 0) {
      if (section.link) {
        // Handle links with or without leading slash
        let cleanLink = section.link.replace(/^\//, '');
        // Handle trailing slashes too
        cleanLink = cleanLink.replace(/\/$/, '');
        const linkFirstSegment = cleanLink.split('/')[0];
        
        return linkFirstSegment === firstPathSegment;
      }
      return false;
    }
    
    // 2. Standard checks for matching items/subitems
    // First check direct matches with section title
    if (currentPath.startsWith(section.title.toLowerCase())) {
      return true;
    }
    
    // Check companyNav (special case)
    const companyNav = data.allMarkdownRemark.edges[0].node.frontmatter.companynav;
    if (section.title.toLowerCase() === 'company' && companyNav && 
        companyNav.some(path => currentPath.startsWith(path))) {
      return true;
    }
    
    // Check for matching items
    if (section.item && section.item.length > 0) {
      for (const item of section.item) {
        if (item.link && currentPath.startsWith(item.link.replace(/^\//, ''))) {
          return true;
        }
        
        // Check subitems
        if (item.subitem && item.subitem.length > 0) {
          for (const subitem of item.subitem) {
            if (subitem.link && currentPath.startsWith(subitem.link.replace(/^\//, ''))) {
              return true;
            }
          }
        }
      }
    }
    
    // Check feature items
    if (section.feature && section.feature.length > 0) {
      for (const feature of section.feature) {
        if (feature.link && currentPath.startsWith(feature.link.replace(/^\//, ''))) {
          return true;
        }
      }
    }
    
    return false;
  };

  const overlay = (
    <div
      id="sidenav-overlay"
      style={{ backgroundColor: 'transparent' }}
    />
  )

  return (
    <>
      <MDBNavbar color="top-nav-collapse" dark expand="lg" fixed="top" scrolling transparent className={`${isScrolled || isMenuItemClicked ? 'scrolled' : ''} ${isMenuItemClicked ? 'menu-clicked' : ''}`}>
        <MDBContainer fluid className="px-lg-6 px-0 pl-i-0">
          <MDBNavbarBrand className="mr-lg-4" onClick={handleNavbarBrandClick}>
            <CustomLink link="/">
              {isScrolled || isMenuItemClicked ? <NavLogoAlt /> : <NavLogo />}
            </CustomLink>
          </MDBNavbarBrand>
          <MDBNavbarToggler aria-label="Mobile Navigation Button" onClick={handleTogglerClick} />
          <MDBCollapse isOpen={collapsed} navbar className="mt-3 mt-lg-0">
            <MDBNavbarNav left>
              {data.allMarkdownRemark.edges[0].node.frontmatter.section.map((section, index) => (
                  <MDBNavItem className="ml-0 py-2 py-lg-0" key={index}>
                    {section.item && section.item.length > 0 ? (
                      <div className="position-static scrollable-dropdown dropdown px-lg-2">
                        <a href="#!" 
                          className={`nav-link dropdown-toggle ${isSectionActive(section) ? 'active' : ''} ${activeDropdown === section.title ? 'active-parent' : ''} ${isMenuItemClicked || isScrolled ? 'text-dark' : ''} `.trim()}
                          onClick={(e) => { handleMenuItemClick(section.title, e); }}
                          aria-expanded={activeDropdown === section.title}
                        >
                          <span className="mr-1">{section.title}</span>
                        </a>
                        <div className={`dropdown-menu dropdown-ug ${activeDropdown === section.title ? 'show' : ''}`}>
                          {/* Add close icon for desktop view */}
                          <div 
                            className="dropdown-close-icon desktop-only" 
                            aria-label="Close dropdown menu"
                            onClick={handleDropdownClose}
                          >
                            <FontAwesomeIcon icon={faTimes} className="fa-hover-spin" />
                          </div>
                          
                          <MDBRow className="d-lg-none">
                            <MDBCol className="pt-4 px-4">
                              <MDBDropdownItem onClick={() => { goBackNav(); }}>
                                <FontAwesomeIcon icon={faArrowLeftLong} className="mr-2" />{section.title}
                              </MDBDropdownItem>
                              
                              {/* Add close icon for mobile view */}
                              <MDBDropdownItem 
                                className="d-lg-none float-right mt-n4" 
                                onClick={handleDropdownClose}
                                style={{cursor: 'pointer'}}
                              >
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                              </MDBDropdownItem>
                              
                              <hr className="nav my-4" />
                            </MDBCol>
                          </MDBRow>
                          <MDBRow className="mx-0">
                            <MDBCol lg="3" md="12" className="bg-feature-item pt-lg-5 pb-lg-4 px-lg-4 d-none d-lg-block">
                              {section.feature.map((feature, index) => (
                                <Fragment key={index}>
                                  {feature.image && (
                                    <GatsbyImage
                                      image={feature.image.childImageSharp.gatsbyImageData}
                                      layout="fixed"
                                      className="pl-ico mb-1 d-none d-md-block"
                                      alt={feature.alttext}
                                    />
                                  )}
                                  {feature.subtitle && (
                                    <MDBDropdownItem className="dropdown-item dropdown-heading-item" href={feature.link ? feature.link : ''}>
                                      {feature.subtitle}
                                    </MDBDropdownItem>
                                  )}
                                  {feature.description && (
                                    <p className="dropdown-feature-item">
                                      {feature.description}
                                    </p>
                                  )}
                                </Fragment>
                              ))}
                            </MDBCol>
                            <MDBCol lg="9" md="12">
                              <MDBRow className="py-lg-5 px-lg-4 pt-4">
                                {section.item.map((item, index) => (
                                  <MDBCol lg={section.collg ? section.collg : '6'} md="12" className={`mb-4 ${item.lgorder ? ` order-lg-${item.lgorder}` : ''}${item.smorder ? ` order-${item.smorder}` : ''}`} key={index}>
                                    <GatsbyImage
                                      image={item.image.childImageSharp.gatsbyImageData}
                                      layout="fixed"
                                      className="pl-ico mb-1 d-none d-md-block"
                                      alt={item.alttext}
                                    />
                                    <MDBDropdownItem className="dropdown-item dropdown-heading-item" href={item.link ? item.link : ''}>
                                      {item.title}
                                    </MDBDropdownItem>

                                    {item.subitem && item.subitem.map((subitem, index) => subitem.title && (
                                      <MDBDropdownItem className="dropdown-item dropdown-sub-item" href={subitem.link} key={index} >
                                        {subitem.title.replace(/[®]/g, "")}
                                      </MDBDropdownItem>
                                    )
                                    )}
                                  </MDBCol>
                                ))}

                                {section.more && (
                                  <MDBCol md="12" className="order-last">
                                    <hr className="nav my-4" />
                                    {section.more && (
                                      <MDBDropdownItem className="dropdown-item dropdown-heading-item" href={section.link}>
                                        {section.more} <FontAwesomeIcon icon={faChevronRight} className="ml-1" />
                                      </MDBDropdownItem>
                                    )}
                                    {(section.feature.length > 1 && section.feature[1].link) && (
                                      <>
                                        {section.feature.map((feature, index) => (index !== 0 && feature.subtitle && (
                                          <MDBDropdownItem className="dropdown-item dropdown-heading-item d-lg-none" href={feature.link ? feature.link : ''} key={index}>
                                            {feature.subtitle} <FontAwesomeIcon icon={faChevronRight} className="ml-1" />
                                          </MDBDropdownItem>
                                        )
                                        ))}
                                      </>
                                    )}
                                  </MDBCol>
                                )
                                }
                              </MDBRow>
                            </MDBCol>
                          </MDBRow>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <MDBDropdownItem 
                          href={section.link} 
                          onClick={() => {handleMenuItemClick()}} 
                          className={`nav-link no-toggle px-lg-2 pr-lg-0 ${section.link && location.pathname.startsWith(resolvePath(section.link)) ? 'active' : ''} ${isMenuItemClicked || isScrolled ? 'text-dark' : ''}`}
                        >
                          {section.title}
                        </MDBDropdownItem>
                      </div>
                    )}
                  </MDBNavItem>
                )
              )}
            </MDBNavbarNav>
            <ul className="navbar-nav right-aligned-nav align-items-lg-center">
              <li className="nav-item py-2 py-lg-0">
                <CustomLink link="/search/" className={`nav-link ${isMenuItemClicked || isScrolled ? 'text-dark' : ''}`} aria-label="Search">
                  Search
                </CustomLink>
              </li>
              <li className="nav-item py-2 py-lg-0">
                <CustomLink link="/contact/" className={`nav-link btn btn-nav-color ml-lg-2 ml-0 hero-btn2 ${isScrolled ? 'text-dark' : ''} ${isMenuItemClicked ? 'menu-open-border' : ''}`.trim()}>
                  Contact us
                </CustomLink>
              </li>
            </ul>
          </MDBCollapse>
        </MDBContainer>
      </MDBNavbar>
      {collapsed && overlay}
    </>
  )
}

export default NavBarPage